import React, { useEffect, useState } from 'react';
import AgentLayout from './AgentLayout';
import axios from 'axios';
import { Plus, Edit, Trash2, X, Users } from 'lucide-react';
import PhoneInput from '../../components/ui/PhoneInput';

const AgentTravellers = () => {
  const [travellers, setTravellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    title: 'Mr',
    first_name: '',
    last_name: '',
    dob: '',
    passport_number: '',
    passport_issue_date: '',
    passport_expiry: '',
    passport_nationality: '',
    email: '',
    phone: ''
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  

  useEffect(() => {
    fetchTravellers();
  }, []);

  const fetchTravellers = async () => {
    try {
      // Using cookie-based auth with withCredentials
      const response = await axios.get(`${API_URL}/api/agent/dashboard/travellers`, { withCredentials: true });
      setTravellers(response.data.travellers);
    } catch (error) {
      console.error('Error fetching travellers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Using cookie-based auth with withCredentials

      if (editMode) {
        // For edit, backend expects JSON body
        await axios.put(
          `${API_URL}/api/agent/dashboard/travellers/${formData.id}`,
          formData,
          { withCredentials: true }
        );
      } else {
        // For create, backend expects query parameters
        const params = new URLSearchParams({
          title: formData.title || 'Mr',
          first_name: formData.first_name,
          last_name: formData.last_name,
          dob: formData.dob,
          passport_number: formData.passport_number || '',
          passport_issue_date: formData.passport_issue_date || '',
          passport_expiry: formData.passport_expiry || '',
          passport_nationality: formData.passport_nationality || '',
          frequent_flyer_number: formData.frequent_flyer_number || '',
          email: formData.email || '',
          phone: formData.phone || ''
        });
        
        await axios.post(
          `${API_URL}/api/agent/dashboard/travellers?${params}`,
          null,
          { withCredentials: true }
        );
      }

      setShowModal(false);
      resetForm();
      fetchTravellers();
    } catch (error) {
      console.error('Error saving traveller:', error);
      alert(`Failed to save traveller: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this traveller?')) return;

    try {
      // Using cookie-based auth with withCredentials
      await axios.delete(`${API_URL}/api/agent/dashboard/travellers/${id}`, { withCredentials: true });
      fetchTravellers();
    } catch (error) {
      console.error('Error deleting traveller:', error);
      alert('Failed to delete traveller');
    }
  };

  const handleEdit = (traveller) => {
    setFormData(traveller);
    setEditMode(true);
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      id: '',
      title: 'Mr',
      first_name: '',
      last_name: '',
      dob: '',
      passport_number: '',
      passport_issue_date: '',
      passport_expiry: '',
      passport_nationality: '',
      email: '',
      phone: ''
    });
    setEditMode(false);
  };

  if (loading) {
    return (
      <AgentLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">My Travellers</h1>
            <p className="text-gray-600 mt-1">Manage your saved passengers for quick booking</p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
          >
            <Plus size={20} />
            Add Traveller
          </button>
        </div>

        {/* Travellers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {travellers.length > 0 ? (
            travellers.map((traveller, index) => (
              <div key={index} className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-800">
                      {traveller.title} {traveller.first_name} {traveller.last_name}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">DOB: {traveller.dob}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(traveller)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(traveller.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
                {traveller.passport_number && (
                  <div className="space-y-1 text-sm">
                    <p className="text-gray-700">
                      <span className="font-medium">Passport:</span> {traveller.passport_number}
                    </p>
                    <p className="text-gray-600">
                      <span className="font-medium">Expiry:</span> {traveller.passport_expiry}
                    </p>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="col-span-full py-12 text-center">
              <Users className="mx-auto text-gray-300 mb-3" size={48} />
              <p className="text-gray-500">No travellers saved yet</p>
              <button
                onClick={() => setShowModal(true)}
                className="mt-4 text-blue-600 hover:underline"
              >
                Add your first traveller
              </button>
            </div>
          )}
        </div>

        {/* Add/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl max-w-2xl w-full p-6 my-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-800">
                  {editMode ? 'Edit Traveller' : 'Add New Traveller'}
                </h2>
                <button onClick={() => { setShowModal(false); resetForm(); }}>
                  <X className="text-gray-500 hover:text-gray-700" size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                    <select
                      required
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="Mr">Mr</option>
                      <option value="Mrs">Mrs</option>
                      <option value="Ms">Ms</option>
                      <option value="Master">Master</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth *</label>
                    <input
                      type="date"
                      required
                      value={formData.dob}
                      onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Passport Number</label>
                    <input
                      type="text"
                      value={formData.passport_number}
                      onChange={(e) => setFormData({ ...formData, passport_number: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Passport Issue Date</label>
                    <input
                      type="date"
                      value={formData.passport_issue_date}
                      onChange={(e) => setFormData({ ...formData, passport_issue_date: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Passport Expiry</label>
                    <input
                      type="date"
                      value={formData.passport_expiry}
                      onChange={(e) => setFormData({ ...formData, passport_expiry: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nationality</label>
                    <input
                      type="text"
                      value={formData.passport_nationality}
                      onChange={(e) => setFormData({ ...formData, passport_nationality: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., IN"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                    <PhoneInput
                      value={formData.phone}
                      onChange={(v) => setFormData({ ...formData, phone: v })}
                      defaultCountry="IN"
                      testid="traveller-phone"
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => { setShowModal(false); resetForm(); }}
                    className="flex-1 px-6 py-3 border border-gray-300 rounded-lg font-semibold hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold"
                  >
                    {editMode ? 'Update' : 'Add'} Traveller
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AgentLayout>
  );
};

export default AgentTravellers;